import {
  EnvelopeIcon,
  LifebuoyIcon,
  PhoneIcon,
  PhoneCallIcon,
  X,
} from '@phosphor-icons/react';
import { createPortal } from 'react-dom';
import { useScreenView } from '../../../../utils/ScreenViewContext';
import { useMemo } from 'react';
import { isPreActiveCustomer } from './home/utils/demoData';
import { useUser } from '../../../../utils/UserContext';
import { CustomerWithDriverOnboarding } from '../../../../types/apiTypes';

const SUPPORT_PHONE_DISPLAY = '+1 (866) USA-TACH';
const SUPPORT_PHONE_TEL = '+18668728224'; // USA-TACH => 872-8224
const SUPPORT_EMAIL = 'support@tachusa.com';
const HELP_CENTER_URL = 'https://help.tachusa.com/';
const EMERGENCY_PHONE_TEL = '+18778853865';

export const SupportModal = ({
  isOpen,
  isFleetNet,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
  isFleetNet?: boolean;
}) => {
  const { screenView } = useScreenView();
  const isMobileView = screenView === 'mobile';
  const { userRecord } = useUser();
  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );

  const PhoneRow = () => {
    const content = (
      <>
        <div className="flex items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7] p-3">
          <PhoneIcon size={24} className="text-graysecondary" />
        </div>
        <p className="text-[16px] font-[400] text-graysecondary">
          {SUPPORT_PHONE_DISPLAY}
        </p>
      </>
    );

    // Click-to-call only on mobile
    return isMobileView ? (
      <a
        href={`tel:${SUPPORT_PHONE_TEL}`}
        className="flex flex-row items-center justify-start gap-x-4 hover:opacity-80"
      >
        {content}
      </a>
    ) : (
      <div className="flex flex-row items-center justify-start gap-x-4">
        {content}
      </div>
    );
  };

  const EmailRow = () => (
    <a
      href={`mailto:${SUPPORT_EMAIL}`}
      className="flex flex-row items-center justify-start gap-x-4 hover:opacity-80"
    >
      <div className="flex items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7] p-3">
        <EnvelopeIcon size={24} className="text-graysecondary" />
      </div>
      <p className="text-[16px] font-[400] text-graysecondary">
        {SUPPORT_EMAIL}
      </p>
    </a>
  );

  const HelpCenterRow = () => (
    <a
      href={HELP_CENTER_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="flex flex-row items-center justify-start gap-x-4 hover:opacity-80"
    >
      <div className="flex items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7] p-3">
        <LifebuoyIcon size={24} className="text-graysecondary" />
      </div>
      <p className="text-[16px] font-[400] text-graysecondary">
        Visit Help Center
      </p>
    </a>
  );

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-auto max-h-[90%] w-full overflow-x-hidden rounded-[4px] bg-white sm:max-w-[95%] md:max-w-[400px]">
              <div className="flex h-[56px] w-full flex-row items-center justify-between p-4 align-middle">
                <h2 className="text-[16px] font-[600] text-primary">
                  Contact Support
                </h2>
                <X
                  className="hover:cursor-pointer hover:opacity-80"
                  onClick={() => {
                    onClose();
                  }}
                  weight="regular"
                  size={24}
                />
              </div>

              <div className="flex flex-col border-y-[1px] border-bordergray p-4">
                <div className="p-4">
                  <PhoneRow />
                </div>
                <div className="p-4">
                  <EmailRow />
                </div>
                <div className="p-4">
                  <HelpCenterRow />
                </div>
              </div>

              {!isPreActive && (
                <div className="flex cursor-pointer items-center justify-center p-4">
                  <a
                    href={`tel:${EMERGENCY_PHONE_TEL}`}
                    className="flex h-[48px] w-full flex-row items-center justify-center gap-2 rounded-full border-[1px] border-danger text-danger hover:bg-red-50"
                  >
                    <PhoneCallIcon size={20} />
                    <p>Get Emergency Help</p>
                  </a>
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return createPortal(renderScreenView(screenView), document.body);
};

export default SupportModal;
